export type EvaluationFormListItem = {
  id: string;
  formTypeId: string;
  category: string;
  title: string;
  /** Quem pode responder. Lista vazia = sem restrição. */
  allowedAnswererTypes?: string[];
  formType?: {
    id: string;
    name: string;
    weight?: number;
  };
};

type CreateFreelancerEvaluationResponse = {
  id: string;
};

// -----------------------------
// Base URL (Vite ou CRA) + /api
// -----------------------------
// "" = mesma origem (relativo). Dev: proxy do package.json; Prod: rewrite do firebase.json.
const DEFAULT_BASE = "";
const DEFAULT_PREFIX = "/api";

function tryGetViteEnv(key: string): string | undefined {
  try {
    return (import.meta as any)?.env?.[key];
  } catch {
    return undefined;
  }
}

function tryGetCraEnv(key: string): string | undefined {
  try {
    return (process as any)?.env?.[key];
  } catch {
    return undefined;
  }
}

function normalizeBase(base: string) {
  return String(base).replace(/\/$/, "");
}

function normalizePrefix(prefix: string) {
  const p = String(prefix || "");
  if (!p) return "";
  return p.startsWith("/") ? p : `/${p}`;
}

// Calcula sob demanda (evita "Cannot access ... before initialization")
function getApiBase(): string {
  const base =
    tryGetViteEnv("VITE_API_BASE") ??
    tryGetCraEnv("REACT_APP_API_BASE") ??
    DEFAULT_BASE;

  const prefix =
    tryGetViteEnv("VITE_API_PREFIX") ??
    tryGetCraEnv("REACT_APP_API_PREFIX") ??
    DEFAULT_PREFIX;

  return `${normalizeBase(base)}${normalizePrefix(prefix)}`;
}

async function readErrorMessage(res: Response): Promise<string> {
  const text = await res.text();
  try {
    const json = JSON.parse(text);
    return json?.message ?? text ?? "Erro na requisição.";
  } catch {
    return text || "Erro na requisição.";
  }
}

function normalizeBearer(token: string): string {
  const t = String(token ?? "").trim();
  return t.toLowerCase().startsWith("bearer ") ? t.slice(7).trim() : t;
}

function buildUrl(path: string, query?: string | URLSearchParams): string {
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  const base = `${getApiBase()}${cleanPath}`;

  if (!query) return base;

  const qs = typeof query === "string" ? query.replace(/^\?/, "") : query.toString();
  return qs ? `${base}?${qs}` : base;
}

export type SubmittedEvaluationAnswerPublic = {
  questionId: string;
  booleanValue?: boolean | null;
  intValue?: number | null;
  textValue?: string | null;
  optionId?: string | null;
  optionIds?: string[] | null;
  question?: {
    id: string;
    type: "BOOLEAN" | "SCALE" | "TEXT" | "SINGLE_CHOICE" | "MULTI_CHOICE";
    prompt: string;
    required?: boolean;
    order?: number;
    minValue?: number | null;
    maxValue?: number | null;
    options?: { id: string; label: string; order?: number }[];
  } | null;
};

export type SubmittedFreelancerEvaluationPublic = {
  id: string;
  submittedAt?: string | null;
  form?: { id: string; version: number; formType?: { name: string } };
  answers?: any[];
};

export type SupervisorStoreListItem = {
  id: string;
  nomeFantasia: string;
  endereco?: string | null;
  estado?: string | null;
};

export type PaginatedStoresResponse = {
  page: number;
  limit: number;
  total: number;
  items: SupervisorStoreListItem[];
};

export async function listEvaluationFormsApi(args: {
  token: string;
  category?: "LOJA" | "FREELANCER";
  includeQuestions?: boolean;
}): Promise<EvaluationFormListItem[]> {
  const token = normalizeBearer(args.token);

  const qs = new URLSearchParams();

  if (args.category) qs.set("category", args.category);
  if (args.includeQuestions !== undefined) {
    qs.set("includeQuestions", String(args.includeQuestions));
  }

  const res = await fetch(buildUrl("/evaluation-forms", qs), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    },
  });

  if (!res.ok) throw new Error(await readErrorMessage(res));

  const data = await res.json();
  return Array.isArray(data) ? data : (data.items ?? []);
}

export async function createFreelancerEvaluationForRegistrationApi(args: {
  token: string;
  registrationId: string;
  formId: string;
}): Promise<CreateFreelancerEvaluationResponse> {
  const token = normalizeBearer(args.token);

  const res = await fetch(
    buildUrl(
      `/freelancer-evaluations/registrations/${encodeURIComponent(args.registrationId)}`,
    ),
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ formId: args.formId }),
    },
  );

  if (!res.ok) throw new Error(await readErrorMessage(res));

  return await res.json();
}

export async function listSubmittedEvaluationsByRegistrationApi(args: {
  token: string;
  registrationId: string;
}): Promise<SubmittedFreelancerEvaluationPublic[]> {
  const token = normalizeBearer(args.token);

  const qs = new URLSearchParams();
  qs.set("registrationId", args.registrationId);
  qs.set("submitted", "true");
  qs.set("includeAnswers", "true");

  const res = await fetch(buildUrl("/freelancer-evaluations", qs), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    },
  });

  if (!res.ok) throw new Error(await readErrorMessage(res));

  const data = await res.json();
  return Array.isArray(data) ? data : (data.items ?? []);
}

export type CreateStoreEvaluationAnswerPayload = {
  questionId: string;
  booleanValue?: boolean | null;
  intValue?: number | null;
  textValue?: string | null;
  dateValue?: string | null;
  optionId?: string | null;
  optionIds?: string[] | null;
};

export type CreateStoreEvaluationPayload = {
  formId?: string;
  answers: CreateStoreEvaluationAnswerPayload[];
};

export type CreateStoreEvaluationResponse = {
  id: string;
};

export async function createStoreEvaluationApi(args: {
  token: string;
  storeId: string;
  payload: CreateStoreEvaluationPayload;
}): Promise<CreateStoreEvaluationResponse> {
  const token = normalizeBearer(args.token);

  const res = await fetch(
    buildUrl(`/stores/${encodeURIComponent(args.storeId)}/evaluations`),
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(args.payload),
    },
  );

  if (!res.ok) throw new Error(await readErrorMessage(res));

  return await res.json();
}

export async function listSupervisorStoresApi(args: {
  token: string;
  nome?: string;
  page?: number;
  limit?: number;
  all?: boolean;
}): Promise<PaginatedStoresResponse> {
  const token = normalizeBearer(args.token);

  const qs = new URLSearchParams();

  if (args.nome) qs.set("nome", args.nome);
  if (args.page) qs.set("page", String(args.page));
  if (args.limit) qs.set("limit", String(args.limit));
  if (args.all !== undefined) qs.set("all", String(args.all));

  const res = await fetch(buildUrl("/stores", qs), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    },
  });

  if (!res.ok) throw new Error(await readErrorMessage(res));

  return await res.json();
}

export async function uploadImageApi(args: {
  token: string;
  file: File;
}): Promise<{ assetId: string; url: string }> {
  const token = normalizeBearer(args.token);
  const formData = new FormData();
  formData.append("file", args.file);

  const res = await fetch(buildUrl("/uploads/images"), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    },
    body: formData,
  });

  if (!res.ok) throw new Error(await readErrorMessage(res));

  return await res.json();
}
